import { useEffect } from 'react';

/**
 * Teach every table how to become a list of cards on a narrow screen.
 *
 * A table needs its column headings to stay meaningful, and on a phone the
 * heading row is off-screen the moment the table is wider than the viewport.
 * The stylesheet solves that by printing the heading in front of each cell —
 * but CSS cannot read another element's text, so the heading has to be copied
 * onto the cell as an attribute first.
 *
 * Doing it here rather than by hand keeps fourteen tables in eight panels from
 * each needing a `data-label` on every `<td>`, and means a table added later is
 * handled without anyone remembering to.
 */
export function useTableLabels(): void {
  useEffect(() => {
    let queued = false;

    const apply = (): void => {
      queued = false;

      for (const table of document.querySelectorAll('table')) {
        const headings = [...table.querySelectorAll('thead th')].map(
          (th) => th.textContent?.trim() ?? '',
        );
        if (headings.length === 0) continue;

        for (const row of table.querySelectorAll('tbody tr')) {
          [...row.children].forEach((cell, index) => {
            const heading = headings[index];
            if (!heading || !(cell instanceof HTMLElement)) return;
            /*
             * A cell marked `data-primary` names the row — a page's name, a
             * person's name — and is left unlabelled on purpose. On a phone the
             * stylesheet gives it the whole card instead of squeezing it into
             * the value half of a label/value split, where a three-word name
             * broke onto three lines beside the chips that belong to it.
             */
            if (cell.dataset.primary !== undefined) return;
            // Written only when it would change something: every write is a
            // mutation, and the observer below is watching for those.
            if (cell.dataset.label !== heading) cell.dataset.label = heading;
          });
        }
      }
    };

    const schedule = (): void => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    apply();

    // React replaces rows wholesale on a refetch, so labels have to be
    // reapplied rather than set once. Coalesced to one pass per frame.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);
}
